import { useDebouncedState } from 'app/hooks/debounce';
import { useAllAddresses } from 'app/network/address';
import { useState } from 'react';
import type { LatLng } from 'react-native-maps';
import {
  DEFAULT_SEARCH_RADIUS_IN_METERS,
  DEFAULT_SHARE_RADIUS_IN_METERS,
  MAX_RADIUS_IN_METERS,
} from 'app/network/preferences';

export default function useMapRadius(addressId?: string, initialRadius?: number) {
  const addressesQuery = useAllAddresses();

  const selectedAddress = addressesQuery.data?.find((address) => address.id === addressId);
  const resetKey = `${selectedAddress?.id ?? ''}:${String(initialRadius ?? '')}`;

  const [radius, setRadius] = useDebouncedState<number>(
    initialRadius ?? selectedAddress?.radius ?? DEFAULT_SHARE_RADIUS_IN_METERS,
    300,
    resetKey,
  );

  const addressStateKey = selectedAddress?.id;
  const initialAddressState = selectedAddress
    ? {
        addressStateKey,
        markerPosition: {
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
        },
        radiusCenter: {
          latitude: selectedAddress.radiusCenterLatitude ?? selectedAddress.latitude,
          longitude: selectedAddress.radiusCenterLongitude ?? selectedAddress.longitude,
        },
      }
    : {
        addressStateKey,
        markerPosition: { latitude: 0, longitude: 0 },
        radiusCenter: { latitude: 0, longitude: 0 },
      };
  const [addressState, setAddressState] = useState(() => initialAddressState);
  let currentAddressState = addressState;
  if (addressState.addressStateKey !== addressStateKey) {
    currentAddressState = initialAddressState;
    setAddressState(currentAddressState);
  }

  const setRadiusCenter = (radiusCenter: LatLng) => {
    setAddressState((current) => ({ ...current, radiusCenter }));
  };

  return {
    selectedAddress,
    markerPosition: currentAddressState.markerPosition,
    radius,
    setRadius,
    radiusCenter: currentAddressState.radiusCenter,
    setRadiusCenter,
    defaults: {
      DEFAULT_SHARE_RADIUS_IN_METERS,
      DEFAULT_SEARCH_RADIUS_IN_METERS,
      DEFAULT_STEP_IN_METERS: 1000,
      LOWER_BOUND_IN_METERS: 1000,
      UPPER_BOUND_IN_METERS: MAX_RADIUS_IN_METERS,
    },
  };
}
